import React, { useState } from 'react';
import { Shield, AlertCircle, Loader2, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';

const STORAGE_KEY = 'hibff_demo_gate_passed';

/**
 * Returns true if this browser session has already passed the demo gate.
 */
export function hasPassedDemoGate() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { validUntil } = JSON.parse(raw);
    return !!validUntil && new Date(validUntil) > new Date();
  } catch {
    return false;
  }
}

export function clearDemoGate() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
}

/**
 * Full-screen passcode gate shown to is_demo users before the app loads.
 */
const DemoGate = ({ onUnlocked }) => {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { user, logout } = useAuth();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.request('/api/auth/demo-verify', {
        method: 'POST',
        body: JSON.stringify({ code: code.trim().toUpperCase() }),
      });
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ validUntil: res.valid_until, label: res.label })
      );
      onUnlocked?.();
    } catch (err) {
      setError(err?.message || 'Invalid passcode');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-gradient-to-br from-[#2D1F3A] via-[#1A1209] to-[#2D1F3A] flex items-center justify-center p-4"
      data-testid="demo-gate"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <Shield size={22} />
            <div>
              <h1 className="text-lg font-bold">HiBFF Demo Access</h1>
              <p className="text-xs opacity-90">Passcode required</p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <p className="text-sm text-gray-700 mb-1">
              Signed in as <span className="font-semibold">{user?.name || 'Alex'}</span>{' '}
              <span className="text-gray-500">({user?.email})</span>
            </p>
            <p className="text-xs text-gray-500">
              This is a demo account. Enter the passcode provided by HiBFF to continue.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5" htmlFor="demo-passcode">
              Passcode
            </label>
            <input
              id="demo-passcode"
              data-testid="demo-gate-input"
              type="text"
              autoComplete="off"
              spellCheck="false"
              inputMode="text"
              placeholder="HIBFF-XXXX-XXXX"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="w-full px-4 py-3 text-center tracking-[0.15em] font-mono text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 bg-gray-50"
              maxLength={20}
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            data-testid="demo-gate-submit-btn"
            disabled={submitting || !code.trim()}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl disabled:opacity-50 hover:shadow-lg flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Verifying…
              </>
            ) : (
              'Enter Demo'
            )}
          </button>

          <button
            type="button"
            onClick={() => logout()}
            data-testid="demo-gate-logout-btn"
            className="w-full py-2.5 text-xs text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1.5"
          >
            <LogOut size={12} /> Sign out
          </button>

          <p className="text-[11px] text-center text-gray-400 leading-relaxed pt-2 border-t border-gray-100">
            This is a sandbox HiBFF environment. Data you enter is not persisted
            beyond this demo session. Demo access is time-limited and logged.
          </p>
        </form>
      </div>
    </div>
  );
};

export default DemoGate;
